// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useBulk from "@/hooks/useBulk";
import useFetch from "@/hooks/useFetch";
import usePageSize from "@/hooks/usePageSize";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useSelection from "@/hooks/useSelection";
import useTranslation from "@/hooks/useTranslation";
import type { TyTeamMember } from "@/types";
import { canManageRole } from "@/utils";
import MemberCard from "@/UI/cards/MemberCard";
import ActionsBar from "@/UI/components/ActionsBar";
import BulkBar from "@/UI/components/BulkBar";
import type { TyBulkField } from "@/UI/components/BulkBar/types";
import EmptyState from "@/UI/components/EmptyState";
import Paginator from "@/UI/components/Paginator";
import ActionsPopup, { type TyAction } from "@/UI/popups/ActionsPopup";
import AddMemberPopup from "@/UI/popups/AddMemberPopup";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import PasswordPopup from "@/UI/popups/PasswordPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
/**
 * Everyone in the active organization. Cards, never a table — the same card reads on a phone and on a
 * desktop, and each row's actions live behind its own menu.
 */
export default function TeamScreen() {
    const t = useTranslation();
    const { role, isOwner } = usePermission();
    const me = useAuth((state) => state.client);
    const addComp = usePopup((state) => state.addComp);

    // The only list in the app with no filters, so its page is state rather than a URL a person
    // would ever send: there is nothing else in the address to keep it company.
    const [page, setPage] = useState(1);
    const pageSize = usePageSize((state) => state.pageSize);

    const list = useFetch<{ members: TyTeamMember[]; total: number }>("/team", {}, false);
    const selection = useSelection();
    const bulk = useBulk({ path: "/team", selection, onDone: () => list.invoke({ query: { limit: pageSize, page } }) });

    // Page and size are the whole query, so one effect owns the loading and nothing else invokes.
    useEffect(() => {
        list.invoke({ query: { limit: pageSize, page } });
        // A paged list is a different list, so the picks made on the old one go with it.
        selection.clear();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize]);
    const action = useFetch<{ password?: string | null }>("/team", {}, false);
    /** Whose row is being acted on, so a returned password can be shown against a name. */
    const [actingOn, setActingOn] = useState<string>("");

    useEffect(() => {
        if (!action.succeededAt) return;
        if (action.data?.password)
            addComp({ name: "password", Component: PasswordPopup, anim: "scale", details: { name: actingOn, password: action.data.password } });
        list.invoke({ query: { limit: pageSize, page } });
        // Keyed on the success timestamp: a second action returning the same status must still refresh.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.succeededAt]);

    const confirm = (details: {
        title: string;
        message: string;
        confirmLabel: string;
        confirmWord?: string;
        danger?: boolean;
        onConfirm: () => void;
    }) => addComp({ name: "confirm", Component: ConfirmPopup, anim: "scale", details });

    const openActions = (member: TyTeamMember, anchor: DOMRect) => {
        const nextRole = member.role === "manager" ? "member" : "manager";
        const actions: TyAction[] = [];
        const run = (options: Parameters<typeof action.invoke>[0]) => {
            setActingOn(member.name);
            action.invoke(options);
        };

        // Only an owner moves anyone between the manager and member tiers.
        if (isOwner && member.role !== "owner") {
            actions.push({
                key: "role",
                label: t.team.actions.turnTo.replace("{role}", t.roles[nextRole]),
                icon: "team",
                onSelect: () =>
                    confirm({
                        title: t.team.actions.turnTo.replace("{role}", t.roles[nextRole]),
                        message: t.team.confirmRole.replace("{name}", member.name).replace("{role}", t.roles[nextRole]),
                        confirmLabel: t.common.confirm,
                        danger: false,
                        onConfirm: () => run({ method: "PATCH", path: `/team/${member._id}/role`, data: { role: nextRole } }),
                    }),
            });
        }

        // A password can only be reissued while they have never set their own.
        if (member.isPasswordTemporary && canManageRole(role, member.role)) {
            actions.push({
                key: "password",
                label: t.team.actions.reissuePassword,
                icon: "refresh",
                onSelect: () =>
                    confirm({
                        title: t.team.actions.reissuePassword,
                        message: t.team.confirmPassword.replace("{name}", member.name),
                        confirmLabel: t.common.confirm,
                        danger: false,
                        onConfirm: () => run({ method: "POST", path: `/team/${member._id}/password` }),
                    }),
            });
        }

        if (isOwner && member.role === "manager") {
            actions.push({
                key: "ownership",
                label: t.team.actions.transferOwnership,
                icon: "crown",
                danger: true,
                onSelect: () =>
                    confirm({
                        title: t.team.actions.transferOwnership,
                        message: t.team.confirmTransfer.replace("{name}", member.name),
                        confirmLabel: t.team.actions.transferOwnership,
                        confirmWord: t.team.transferWord,
                        onConfirm: () => run({ method: "POST", path: `/team/${member._id}/ownership` }),
                    }),
            });
        }

        /**
         * `select` is offered only where this role could act on them anyway — a person nobody here
         * may touch would sit in a selection that every write then skips.
         */
        if (canManageRole(role, member.role)) {
            actions.push({ key: "select", label: t.bulk.select, icon: "check", onSelect: () => selection.toggle(member._id) });
            actions.push({
                key: "remove",
                label: t.team.actions.remove,
                icon: "trash",
                danger: true,
                onSelect: () =>
                    confirm({
                        title: t.team.actions.remove,
                        message: t.team.confirmRemove.replace("{name}", member.name),
                        confirmLabel: t.team.actions.remove,
                        onConfirm: () => run({ method: "DELETE", path: `/team/${member._id}` }),
                    }),
            });
        }

        if (!actions.length) return;
        addComp({
            name: "member-actions",
            Component: ActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            details: { actions },
            anchor: { top: anchor.top, left: anchor.left, right: anchor.right, bottom: anchor.bottom, width: anchor.width, height: anchor.height },
        });
    };

    const openAdd = () =>
        addComp({
            name: "add-member",
            Component: AddMemberPopup,
            place: "end",
            anim: "endSlide",
            details: {
                onDone: ({ name, password }: { name: string; password: string | null }) => {
                    if (password) addComp({ name: "password", Component: PasswordPopup, anim: "scale", details: { name, password } });
                    list.invoke();
                },
            },
        });

    /**
     * **`role` is the only field.** A name, a phone and a password belong to the person rather than
     * to the membership, and the first two are not this company's to write — one person keeps one
     * identity across every organization they join. Ownership is not a role either: it moves by
     * transfer, to one named person.
     *
     * Only an owner is offered it, the same gate the single role change has.
     */
    const bulkFields: TyBulkField[] = [
        {
            key: "role",
            label: t.team.role,
            kind: "select",
            options: [
                { value: "manager", label: t.roles.manager },
                { value: "member", label: t.roles.member },
            ],
        },
    ];

    return (
        <section className="team-screen">
            <ActionsBar addLabel={t.team.add} onAdd={openAdd} />

            {action.error && <p className="team-screen__state team-screen__state--error">{action.error}</p>}
            {list.loading && <p className="team-screen__state">{t.common.loading}</p>}
            {list.error && <p className="team-screen__state team-screen__state--error">{list.error}</p>}
            {!list.loading && !list.error && !list.data?.members.length && (
                <EmptyState icon="team" title={t.team.empty} description={t.team.emptyHint} actionLabel={t.team.add} onAction={openAdd} />
            )}

            <div className="team-screen__grid">
                {list.data?.members.map((member) => (
                    <MemberCard
                        key={member._id}
                        member={member}
                        isMe={member._id === me?._id}
                        canManage={member._id !== me?._id && (canManageRole(role, member.role) || (isOwner && member.role === "manager"))}
                        onActions={(anchor) => openActions(member, anchor)}
                        isSelected={selection.has(member._id)}
                        isPicking={selection.isSelecting}
                        // A person is not a record you open, so pressing the card means something
                        // only while the bar is up — and never on somebody this role cannot touch.
                        onPick={
                            selection.isSelecting && member._id !== me?._id && canManageRole(role, member.role)
                                ? () => selection.toggle(member._id)
                                : undefined
                        }
                    />
                ))}
            </div>

            {/* One or the other, never both — `hooks/useSelection` says why the pager goes away. */}
            {selection.isSelecting ? (
                <BulkBar
                    count={selection.count}
                    fields={isOwner ? bulkFields : undefined}
                    busy={bulk.busy}
                    note={bulk.note}
                    onClear={selection.clear}
                    onApply={isOwner ? (set) => bulk.apply(set) : undefined}
                    deleteLabel={t.team.actions.remove}
                    onDelete={() =>
                        bulk.confirmed({
                            title: t.team.actions.remove,
                            message: t.bulk.confirmRemove.replace("{count}", String(selection.count)),
                            confirmLabel: t.team.actions.remove,
                            doneLabel: t.bulk.removed,
                        })
                    }
                />
            ) : (
                <Paginator page={page} total={list.data?.total ?? 0} count={list.data?.members.length ?? 0} onPage={setPage} />
            )}
        </section>
    );
}
